import { DateTime, LocaleOptions } from 'luxon'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'

type FormatDateOptions = {
  date: string
  type:
    | 'mm/dd'
    | 'mm/dd/yyyy'
    | 'mm/dd/yy'
    | 'mm/dd/yyyy-hh:mm'
    | 'mm/dd/yyyy-hh:mm-ss'
    | 'time-with-zone'
    | 'mm/dd/yy-time-with-zone'
  locale?: LocaleOptions['locale']
}

export const formatDate = ({ date, type, locale = 'en-US' }: FormatDateOptions) => {
  const dateTime = DateTime.fromISO(date)
  invariant(dateTime.isValid, `dateTime is invalid: '${date}'`)

  return match(type)
    .with('mm/dd', () =>
      dateTime.toLocaleString(
        {
          month: 'numeric',
          day: 'numeric',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yyyy', () =>
      dateTime.toLocaleString(
        {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yy', () =>
      dateTime.toLocaleString(
        {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yyyy-hh:mm', () => dateTime.toLocal().toFormat('MM/dd/yyyy · hh:mm a', { locale }))
    .with('mm/dd/yyyy-hh:mm-ss', () => dateTime.toLocal().toFormat('MM/dd/yyyy - hh:mm:ss a', { locale }))
    .with('time-with-zone', () =>
      dateTime.toLocaleString(
        {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yy-time-with-zone', () => dateTime.toFormat('M/d/yy hh:mm:ss a ZZZZ'))
    .exhaustive()
}

export const formatRelativeTime = (date: string): string => {
  const dateTime = DateTime.fromISO(date)
  invariant(dateTime.isValid, `dateTime is invalid: '${date}'`)

  return dateTime.toRelative()
}

export const formatDuration = (startIso: string, endIso: string): string => {
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  invariant(start.isValid, `start date is invalid: '${startIso}'`)
  invariant(end.isValid, `end date is invalid: '${endIso}'`)

  const diff = end.diff(start, ['hours', 'minutes', 'seconds']).mapUnits(Math.floor).rescale()
  return diff.toHuman({ unitDisplay: 'narrow' }).replace(/,\s*/g, ' ') || '0s'
}

/**
 * Whole days between `iso` and `now`, floored. `now` is a parameter rather than read from the clock so
 * callers that bucket by age stay deterministic under test.
 *
 * Returns `undefined` for an unparseable timestamp rather than asserting: the threat model's
 * `generatedAt` is optional on the wire, and one bad value should not take a whole estate roll-up down.
 */
export const daysSince = (iso: string, now: string): number | undefined => {
  const then = DateTime.fromISO(iso)
  const reference = DateTime.fromISO(now)
  if (!then.isValid || !reference.isValid) {
    // Logged the way `isIsoTimestampInPast` below logs the same condition: the value is still swallowed
    // so one bad timestamp cannot take an estate roll-up down, but it should not vanish silently.
    console.error(`timestamp is invalid: '${then.isValid ? now : iso}'`)
    return undefined
  }

  return Math.floor(reference.diff(then, 'days').days)
}

export const isIsoTimestampInPast = (timestamp?: string) => {
  if (!timestamp) {
    return false
  }

  const dateTime = DateTime.fromISO(timestamp)
  if (!dateTime.isValid) {
    console.error(`timestamp is invalid: '${timestamp}'`)
    return false
  }

  return DateTime.now() > dateTime
}
